import React, { useCallback, useEffect, useState, createElement } from 'react';
import { RotateCcwIcon, SearchIcon, CalendarIcon, EyeIcon, PrinterIcon, PackageIcon, BuildingIcon, UserIcon, BanIcon, PlusIcon, XIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { supabaseAdmin } from '../lib/supabase';
import { Visit, DevolucionItem, Product } from '../types';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Textarea } from '../components/ui/Textarea';
import jsPDF from 'jspdf';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png';
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
async function printReciboDevolucion(visit: Visit, codigoCliente: string) {
  const pw = 80;
  const ml = 4;
  const mr = 4;
  const cw = pw - ml - mr;
  const cx = pw / 2;
  const items = visit.devolucionItems || [];
  const totalUnidades = items.reduce((sum, item) => sum + item.cantidad, 0);
  // Calculate dynamic height based on items
  const baseHeight = 160;
  const rowHeight = 18; // each product takes ~18mm (name + sku + details)
  const ph = Math.max(baseHeight + items.length * rowHeight, 200);
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [pw, ph]
  });
  const fmtDate = (d: Date) => new Date(d).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
  const fmtDateLong = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  const dashedLine = (y: number) => {
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(ml, y, pw - mr, y);
    doc.setLineDashPattern([], 0);
  };
  let y = 2;
  // ── LOGO + HEADER ─────────────────────────────────────────────────────────
  const logoBase64 = await getLogoBase64();
  if (logoBase64) {
    const logoSize = 14;
    doc.addImage(logoBase64, 'PNG', cx - logoSize / 2, y, logoSize, logoSize);
    y += logoSize + 2;
  }
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('MILADYS RODRIGUEZ & ASO.', cx, y, {
    align: 'center'
  });
  y += 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Av. Antonio Guzmán No.102 • 809-247-1006', cx, y, {
    align: 'center'
  });
  y += 4;
  doc.text('Santiago, Rep. Dom.', cx, y, {
    align: 'center'
  });
  y += 4;
  doc.text('RNC: 130-329291', cx, y, {
    align: 'center'
  });
  y += 5;
  dashedLine(y);
  y += 5;
  // ── TITLE ─────────────────────────────────────────────────────────────────
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(180, 30, 30);
  doc.text('RECIBO DE DEVOLUCIÓN', cx, y, {
    align: 'center'
  });
  doc.setTextColor(0, 0, 0);
  y += 6;
  // ── RECEIPT NUMBER & DATE ─────────────────────────────────────────────────
  const receiptNum = 'DEV-' + visit.id.replace('temp-', '').replace(/-/g, '').slice(-4).toUpperCase();
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('No: ' + receiptNum, ml, y);
  doc.setFont('helvetica', 'normal');
  doc.text('Fecha: ' + fmtDate(visit.fecha), pw - mr, y, {
    align: 'right'
  });
  y += 5;
  dashedLine(y);
  y += 5;
  // ── CLIENT & VENDEDOR ─────────────────────────────────────────────────────
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Cliente:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(visit.clienteNombre, ml + 14, y);
  y += 5;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Código:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(codigoCliente || '-', ml + 14, y);
  y += 5;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Zona:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(visit.zona || '-', ml + 14, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('Vendedor:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(visit.vendedorNombre, ml + 18, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Motivo: Productos vencidos / devolución', ml, y);
  y += 5;
  dashedLine(y);
  y += 4;
  // ── PRODUCTS TABLE ────────────────────────────────────────────────────────
  // Header — white background with bold black text for thermal printer compatibility
  const tRowH = 5.5;
  doc.setFillColor(255, 255, 255);
  doc.rect(ml, y, cw, tRowH, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(ml, y, cw, tRowH, 'S');
  doc.setLineWidth(0.5);
  doc.line(ml, y + tRowH, ml + cw, y + tRowH);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  // Columns: PRODUCTO (30) | CANT (10) | LOTE (16) | VENCE (16) = 72
  const colWidths = [30, 10, 16, 16];
  const colHeaders = ['PRODUCTO', 'CANT', 'LOTE', 'VENCE'];
  let tx = ml;
  colHeaders.forEach((col, i) => {
    doc.text(col, tx + colWidths[i] / 2, y + tRowH - 1.2, {
      align: 'center'
    });
    tx += colWidths[i];
  });
  y += tRowH;
  // Data rows
  doc.setTextColor(0, 0, 0);
  items.forEach((item, idx) => {
    const isEven = idx % 2 === 0;
    const nameRowH = 5;
    const skuRowH = 3.5;
    const totalItemH = nameRowH + skuRowH + 1;
    // Background
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 255);
    doc.rect(ml, y, cw, totalItemH, 'F');
    doc.setDrawColor(220, 225, 230);
    doc.setLineWidth(0.1);
    doc.line(ml, y + totalItemH, pw - mr, y + totalItemH);
    // Product name (truncated to fit)
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    const maxNameWidth = colWidths[0] - 2;
    let displayName = item.productoNombre;
    while (doc.getTextWidth(displayName) > maxNameWidth && displayName.length > 3) {
      displayName = displayName.slice(0, -1);
    }
    if (displayName !== item.productoNombre) displayName += '..';
    doc.text(displayName, ml + 1, y + nameRowH - 0.5);
    // SKU below name
    doc.setFontSize(5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(item.productoSku, ml + 1, y + nameRowH + skuRowH - 1);
    doc.setTextColor(0, 0, 0);
    // Cantidad - centered, bold
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(String(item.cantidad), ml + colWidths[0] + colWidths[1] / 2, y + totalItemH / 2 + 1.5, {
      align: 'center'
    });
    // Lote
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text(item.lote || '-', ml + colWidths[0] + colWidths[1] + colWidths[2] / 2, y + totalItemH / 2 + 1.5, {
      align: 'center'
    });
    // Fecha vencimiento
    doc.text(item.fechaVencimiento ? fmtDateLong(item.fechaVencimiento) : '-', ml + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] / 2, y + totalItemH / 2 + 1.5, {
      align: 'center'
    });
    y += totalItemH;
  });
  // ── TOTAL ROW ─────────────────────────────────────────────────────────────
  const totalRowH = 7;
  doc.setFillColor(240, 245, 255);
  doc.rect(ml, y, cw, totalRowH, 'F');
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.3);
  doc.rect(ml, y, cw, totalRowH, 'S');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('TOTAL UNIDADES:', ml + 2, y + totalRowH - 2);
  doc.setFontSize(10);
  doc.text(String(totalUnidades), pw - mr - 2, y + totalRowH - 1.5, {
    align: 'right'
  });
  doc.setTextColor(0, 0, 0);
  y += totalRowH + 4;
  dashedLine(y);
  y += 7;
  // ── SIGNATURE LINES ───────────────────────────────────────────────────────
  const sigW = 28;
  doc.setLineWidth(0.3);
  doc.line(ml, y, ml + sigW, y);
  doc.line(pw - mr - sigW, y, pw - mr, y);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Entregado (Cliente)', ml + sigW / 2, y + 4, {
    align: 'center'
  });
  doc.text('Recibido (Vendedor)', pw - mr - sigW / 2, y + 4, {
    align: 'center'
  });
  y += 10;
  dashedLine(y);
  y += 6;
  // ── FOOTER ────────────────────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('--- Devolución Registrada ---', cx, y, {
    align: 'center'
  });
  y += 7;
  // Barcode simulation
  const barY = y;
  const barH = 10;
  let bx = ml;
  const seed = receiptNum.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  for (let i = 0; i < 60; i++) {
    const w = seed * (i + 7) * 13 % 3 * 0.4 + 0.5;
    if (i % 2 === 0) {
      doc.setFillColor(0, 0, 0);
      doc.rect(bx, barY, w, barH, 'F');
    }
    bx += w + 0.3;
    if (bx > ml + cw - 2) break;
  }
  y += barH + 4;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RECIBO: ' + receiptNum, cx, y, {
    align: 'center'
  });
  window.open(String(doc.output('bloburl')), '_blank');
}
export function DevolucionesPage() {
  const {
    clients,
    products: contextProducts,
    loadProducts
  } = useApp();
  const {
    user
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  // ── Own data loading from Supabase (not limited by AppContext's 300 cap) ──
  const [devVisits, setDevVisits] = useState<Visit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [anularVisit, setAnularVisit] = useState<Visit | null>(null);
  const [isAnulando, setIsAnulando] = useState(false);
  const [anulados, setAnulados] = useState<Set<string>>(new Set());
  // ── New Devolucion Modal State ──
  const [isNewDevModalOpen, setIsNewDevModalOpen] = useState(false);
  const [newDevClient, setNewDevClient] = useState('');
  const [newDevItems, setNewDevItems] = useState<DevolucionItem[]>([]);
  const [newDevNotas, setNewDevNotas] = useState('');
  const [isSavingDev, setIsSavingDev] = useState(false);
  const [inventoryProducts, setInventoryProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [devProductSearch, setDevProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const mapVisitRow = (row: any): Visit => ({
    id: row.id,
    vendedorId: row.vendedor_id,
    vendedorNombre: row.vendedor_nombre,
    clienteId: row.cliente_id,
    clienteNombre: row.cliente_nombre,
    zona: row.zona,
    tipoVisita: row.tipo_visita,
    fecha: new Date(row.fecha),
    horaLlegada: row.hora_llegada ? new Date(row.hora_llegada) : null,
    horaSalida: row.hora_salida ? new Date(row.hora_salida) : null,
    tiempoTotal: row.tiempo_total,
    ubicacionLlegada: row.ubicacion_llegada ? {
      ...row.ubicacion_llegada,
      timestamp: new Date(row.ubicacion_llegada.timestamp)
    } : null,
    ubicacionSalida: row.ubicacion_salida ? {
      ...row.ubicacion_salida,
      timestamp: new Date(row.ubicacion_salida.timestamp)
    } : null,
    cobro: {
      huboCobo: false,
      montoCobrado: 0,
      metodoPago: null,
      numeroFactura: '',
      estadoFactura: 'no_pago',
      credito: 0,
      descuento: 0,
      ...(row.cobro || {})
    },
    venta: {
      huboVenta: false,
      montoVenta: 0,
      numeroPedido: '',
      ...(row.venta || {})
    },
    comentarios: row.comentarios || '',
    motivoNoPago: row.motivo_no_pago || '',
    productosVencidos: row.productos_vencidos ?? null,
    devolucionItems: row.devolucion_items || [],
    proximaVisita: row.proxima_visita ? new Date(row.proxima_visita) : null,
    estado: row.estado,
    numeroRecibo: row.numero_recibo || null
  });
  const loadDevoluciones = async () => {
    setIsLoading(true);
    try {
      const {
        data
      } = await supabaseAdmin.from('visits').select('*').not('devolucion_items', 'is', null).order('fecha', {
        ascending: false
      });
      if (data) {
        // Filter for visits that actually have devolucion items (non-empty array)
        const mapped = data.map(mapVisitRow).filter(v => v.devolucionItems && v.devolucionItems.length > 0);
        setDevVisits(mapped);
        // Initialize anulados set
        const anulSet = new Set<string>();
        data.forEach(row => {
          if (row.devolucion_anulada) anulSet.add(row.id);
        });
        setAnulados(anulSet);
      }
    } catch (err) {
      console.error('Error loading devoluciones:', err);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    loadDevoluciones();
  }, []);
  // Auto-refresh when visits change in Supabase
  const loadDevolucionesCallback = useCallback(() => {
    loadDevoluciones();
  }, []);
  useRealtimeRefresh(['visits'], loadDevolucionesCallback);
  // Fetch products when modal opens
  const [productsFetchError, setProductsFetchError] = useState('');
  useEffect(() => {
    if (!isNewDevModalOpen) return;
    // Use shared products from context instead of independent fetch
    const fetchProducts = async () => {
      setIsLoadingProducts(true);
      setProductsFetchError('');
      try {
        await loadProducts();
        // Products will be synced via the effect below
      } catch (err: any) {
        console.error('Error fetching products:', err);
        setProductsFetchError(`Error inesperado: ${err?.message || 'desconocido'}`);
      } finally {
        setIsLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [isNewDevModalOpen]);
  // Sync context products to local inventoryProducts state
  useEffect(() => {
    if (contextProducts.length > 0) {
      setInventoryProducts(contextProducts);
      setProductsFetchError('');
    }
  }, [contextProducts]);
  const handleAnularDevolucion = async () => {
    if (!anularVisit) return;
    setIsAnulando(true);
    try {
      await supabaseAdmin.from('visits').update({
        devolucion_anulada: true
      }).eq('id', anularVisit.id);
      setAnulados(prev => new Set([...prev, anularVisit.id]));
      setAnularVisit(null);
    } catch (err) {
      console.error('Error anulando devolución:', err);
    } finally {
      setIsAnulando(false);
    }
  };
  const handleSaveNewDevolucion = async () => {
    if (!newDevClient || newDevItems.length === 0 || !user) return;
    setIsSavingDev(true);
    try {
      const client = clients.find(c => c.id === newDevClient);
      if (!client) throw new Error('Cliente no encontrado');
      // 1. Calculate total value
      const totalDevolucionValue = newDevItems.reduce((sum, item) => sum + item.cantidad * (item.precioUnitario || 0), 0);
      // 2. Create visit record
      const tempId = `temp-${Date.now()}`;
      const visitPayload = {
        vendedor_id: user.id,
        vendedor_nombre: user.nombre,
        cliente_id: client.id,
        cliente_nombre: client.nombre,
        zona: client.zona || '',
        tipo_visita: 'seguimiento',
        fecha: new Date().toISOString(),
        hora_llegada: new Date().toISOString(),
        hora_salida: new Date().toISOString(),
        tiempo_total: 0,
        estado: 'completada',
        productos_vencidos: true,
        devolucion_items: newDevItems,
        comentarios: newDevNotas,
        cobro: {
          huboCobo: false,
          montoCobrado: 0,
          metodoPago: null,
          numeroFactura: '',
          estadoFactura: 'no_pago',
          credito: 0,
          descuento: 0
        },
        venta: {
          huboVenta: false,
          montoVenta: 0,
          numeroPedido: ''
        }
      };
      const {
        data: visitData,
        error: visitError
      } = await supabaseAdmin.from('visits').insert(visitPayload).select().single();
      if (visitError) throw visitError;
      // 3. Update stock
      const productIds = newDevItems.map(item => item.productoId);
      const {
        data: currentProducts
      } = await supabaseAdmin.from('products').select('id, stock').in('id', productIds);
      if (currentProducts) {
        await Promise.all(newDevItems.map(async item => {
          const currentProduct = currentProducts.find((p: any) => p.id === item.productoId);
          if (currentProduct) {
            const newStock = (currentProduct.stock || 0) + item.cantidad;
            await supabaseAdmin.from('products').update({
              stock: newStock
            }).eq('id', item.productoId);
          }
        }));
      }
      // 4. Create credit note
      if (totalDevolucionValue > 0) {
        const ts = Date.now().toString(36).toUpperCase();
        const rnd = Math.random().toString(36).slice(2, 6).toUpperCase();
        const ncNumero = `NC-DEV-${ts}-${rnd}`;
        const productosDesc = newDevItems.map(item => `${item.productoNombre} x${item.cantidad}`).join(', ');
        const ncPayload = {
          numero: ncNumero,
          farmacia: client.nombre,
          vendedor: user.nombre,
          vendedor_id: user.id,
          monto: Math.round(totalDevolucionValue * 100) / 100,
          monto_disponible: Math.round(totalDevolucionValue * 100) / 100,
          estado: 'disponible',
          notas: `Devolución manual: ${productosDesc}${newDevNotas ? ` | Notas: ${newDevNotas}` : ''}`
        };
        const {
          error: ncError
        } = await supabaseAdmin.from('credit_notes').insert(ncPayload);
        if (ncError) {
          // Retry once
          const rnd2 = Math.random().toString(36).slice(2, 8).toUpperCase();
          ncPayload.numero = `NC-DEV-${ts}-${rnd2}`;
          await supabaseAdmin.from('credit_notes').insert(ncPayload);
        }
      }
      // Reset and close
      setIsNewDevModalOpen(false);
      setNewDevClient('');
      setNewDevItems([]);
      setNewDevNotas('');
      setDevProductSearch('');
      loadDevoluciones();
    } catch (err) {
      console.error('Error saving devolution:', err);
      alert('Error al guardar la devolución');
    } finally {
      setIsSavingDev(false);
    }
  };
  const ITEMS_PER_PAGE = 10;
  // Apply search and role filters — now using devVisits instead of AppContext visits
  const filteredVisits = devVisits.filter(v => isAdmin || v.vendedorId === user?.id).filter(v => {
    if (dateFilter) {
      const visitDate = new Date(v.fecha).toISOString().split('T')[0];
      if (visitDate !== dateFilter) return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return v.clienteNombre.toLowerCase().includes(query) || v.vendedorNombre.toLowerCase().includes(query) || v.devolucionItems?.some(item => item.productoNombre.toLowerCase().includes(query) || item.productoSku.toLowerCase().includes(query));
    }
    return true;
  });
  // Sorting setup
  const {
    items: sortedVisits,
    requestSort,
    sortConfig
  } = useSortableData(filteredVisits, {
    fecha: v => new Date(v.fecha).getTime(),
    cliente: v => v.clienteNombre,
    vendedor: v => v.vendedorNombre,
    cantidad: v => v.devolucionItems?.reduce((sum, item) => sum + item.cantidad, 0) || 0,
    valor: v => v.devolucionItems?.reduce((sum, item) => sum + item.cantidad * ((item as any).precioUnitario || 0), 0) || 0
  });
  const finalVisits = sortConfig ? sortedVisits : [...sortedVisits].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalPages = Math.ceil(finalVisits.length / ITEMS_PER_PAGE);
  const paginatedVisits = finalVisits.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const formatTime = (date: Date | null) => date ? new Date(date).toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit'
  }) : '--:--';
  const handlePrintRecibo = async (visit: Visit) => {
    setPrintingId(visit.id);
    try {
      const client = clients.find(c => c.id === visit.clienteId);
      const codigoCliente = client?.codigo || client?.id?.slice(0, 8) || '-';
      await printReciboDevolucion(visit, codigoCliente);
    } finally {
      setPrintingId(null);
    }
  };
  return <div className="space-y-6" data-id="element-1633">
      <PageHeader title="Devoluciones" subtitle="Registro de productos devueltos o vencidos" icon={<RotateCcwIcon className="w-6 h-6" data-id="element-1635" />} data-id="element-1634">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto" data-id="element-1636">
          <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1638" />} onClick={() => setIsNewDevModalOpen(true)} className="w-full sm:w-auto" data-id="element-1637">
            Nueva Devolución
          </Button>
          <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-1640" />} data-id="element-1639" />
          <div className="relative w-full sm:w-64" data-id="element-1641">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1642" />
            <input type="text" placeholder="Buscar cliente o producto..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" data-id="element-1643" />
          </div>
        </div>
      </PageHeader>

      {/* Table */}
      <Card padding="none" className="animate-stagger-1" data-id="element-1644">
        {isLoading ? <div className="p-16 text-center" data-id="element-1645">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-50 rounded-2xl mb-4" data-id="element-1646">
              <div className="w-8 h-8 border-3 border-rose-200 border-t-rose-600 rounded-full animate-spin" data-id="element-1647" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-1648">
              Cargando devoluciones...
            </h3>
          </div> : filteredVisits.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-1649">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-50 rounded-2xl mb-4" data-id="element-1650">
              <RotateCcwIcon className="w-8 h-8 text-rose-400" data-id="element-1651" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-1652">
              No hay devoluciones
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-1653">
              {dateFilter || searchQuery ? 'No se encontraron devoluciones con los filtros aplicados' : 'Aún no hay devoluciones registradas en el sistema'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-1654">
            <table className="w-full" data-id="element-1655">
              <thead data-id="element-1656">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-1657">
                  <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1658" />
                  <SortableTableHeader label="Cliente" sortKey="cliente" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1659" />
                  {isAdmin && <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1660" />}
                  <SortableTableHeader label="Total Productos" sortKey="cantidad" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1661" />
                  <SortableTableHeader label="Valor" sortKey="valor" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1662" />
                  <th className="px-5 py-4 w-24" data-id="element-1663"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1664">
                {paginatedVisits.map(visit => {
              const totalItems = visit.devolucionItems?.reduce((sum, item) => sum + item.cantidad, 0) || 0;
              const uniqueProducts = visit.devolucionItems?.length || 0;
              const isPrinting = printingId === visit.id;
              return <tr key={visit.id} className="hover:bg-rose-50/30 transition-colors cursor-pointer" onClick={() => setSelectedVisit(visit)} data-id="element-1665">
                      <td className="px-5 py-4" data-id="element-1666">
                        <p className="font-medium text-slate-900 text-sm" data-id="element-1667">
                          {formatDate(visit.fecha)}
                        </p>
                        <p className="text-xs text-slate-400" data-id="element-1668">
                          {formatTime(visit.horaLlegada)}
                        </p>
                      </td>
                      <td className="px-5 py-4" data-id="element-1669">
                        <p className="font-medium text-slate-900 text-sm" data-id="element-1670">
                          {visit.clienteNombre}
                        </p>
                        <p className="text-xs text-slate-400" data-id="element-1671">{visit.zona}</p>
                      </td>
                      {isAdmin && <td className="px-5 py-4 text-sm text-slate-700" data-id="element-1672">
                          {visit.vendedorNombre}
                        </td>}
                      <td className="px-5 py-4 text-center" data-id="element-1673">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-rose-50 text-rose-700" data-id="element-1674">
                          <PackageIcon className="w-3.5 h-3.5" data-id="element-1675" />
                          <span className="font-bold text-sm" data-id="element-1676">
                            {totalItems}
                          </span>
                          <span className="text-xs opacity-80" data-id="element-1677">
                            ({uniqueProducts} ref)
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right" data-id="element-1678">
                        {(() => {
                    const totalVal = visit.devolucionItems?.reduce((sum, item) => sum + item.cantidad * ((item as any).precioUnitario || 0), 0) || 0;
                    return totalVal > 0 ? <span className="font-bold text-rose-700 text-sm" data-id="element-1679">
                              RD$
                              {totalVal.toLocaleString('es-DO', {
                        minimumFractionDigits: 2
                      })}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-1680">—</span>;
                  })()}
                      </td>
                      <td className="px-5 py-4" data-id="element-1681">
                        <div className="flex items-center gap-1" data-id="element-1682">
                          <Button variant="ghost" size="sm" onClick={e => {
                      e.stopPropagation();
                      setSelectedVisit(visit);
                    }} data-id="element-1683">
                            <EyeIcon className="w-4 h-4 text-slate-400" data-id="element-1684" />
                          </Button>
                          {!anulados.has(visit.id) && <>
                              <button onClick={e => {
                        e.stopPropagation();
                        handlePrintRecibo(visit);
                      }} disabled={isPrinting} title="Imprimir recibo" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors disabled:opacity-40" data-id="element-1685">
                                {isPrinting ? <div className="w-4 h-4 border-2 border-rose-200 border-t-rose-600 rounded-full animate-spin" data-id="element-1686" /> : <PrinterIcon className="w-4 h-4" data-id="element-1687" />}
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setAnularVisit(visit);
                      }} title="Anular recibo" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1688">
                                <BanIcon className="w-4 h-4" data-id="element-1689" />
                              </button>
                            </>}
                          {anulados.has(visit.id) && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-500" data-id="element-1690">
                              <BanIcon className="w-3 h-3" data-id="element-1691" /> Anulado
                            </span>}
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </Card>

      <div className="animate-stagger-2" data-id="element-1692">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredVisits.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1693" />
      </div>

      {/* Detail Modal */}
      <Modal isOpen={!!selectedVisit} onClose={() => setSelectedVisit(null)} title="Detalle de Devolución" size="lg" data-id="element-1694">
        {selectedVisit && <>
            <ModalBody data-id="element-1695">
              <div className="space-y-6" data-id="element-1696">
                {/* Header Info */}
                <div className="grid grid-cols-2 gap-4" data-id="element-1697">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1698">
                    <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0" data-id="element-1699">
                      <BuildingIcon className="w-5 h-5 text-indigo-600" data-id="element-1700" />
                    </div>
                    <div data-id="element-1701">
                      <p className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-1702">
                        Cliente
                      </p>
                      <p className="font-bold text-slate-900 text-sm" data-id="element-1703">
                        {selectedVisit.clienteNombre}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-1704">
                        {selectedVisit.zona}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1705">
                    <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0" data-id="element-1706">
                      <UserIcon className="w-5 h-5 text-emerald-600" data-id="element-1707" />
                    </div>
                    <div data-id="element-1708">
                      <p className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-1709">
                        Vendedor
                      </p>
                      <p className="font-bold text-slate-900 text-sm" data-id="element-1710">
                        {selectedVisit.vendedorNombre}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-1711">
                        {formatDate(selectedVisit.fecha)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Products List */}
                <div data-id="element-1712">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1713">
                    <PackageIcon className="w-5 h-5 text-rose-500" data-id="element-1714" />
                    Productos Devueltos
                  </h4>
                  <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1715">
                    <table className="w-full text-sm" data-id="element-1716">
                      <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1717">
                        <tr data-id="element-1718">
                          <th className="px-4 py-3 text-left font-semibold text-slate-600" data-id="element-1719">
                            Producto
                          </th>
                          <th className="px-4 py-3 text-center font-semibold text-slate-600" data-id="element-1720">
                            Cantidad
                          </th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-600" data-id="element-1721">
                            Precio Unit.
                          </th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-600" data-id="element-1722">
                            Valor
                          </th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-600" data-id="element-1723">
                            Lote
                          </th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-600" data-id="element-1724">
                            Vencimiento
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-1725">
                        {selectedVisit.devolucionItems?.map((item, idx) => {
                      const precio = (item as any).precioUnitario || 0;
                      const valor = item.cantidad * precio;
                      return <tr key={idx} className="bg-white" data-id="element-1726">
                              <td className="px-4 py-3" data-id="element-1727">
                                <p className="font-semibold text-slate-900" data-id="element-1728">
                                  {item.productoNombre}
                                </p>
                                <p className="text-xs text-slate-500 font-mono" data-id="element-1729">
                                  {item.productoSku}
                                </p>
                              </td>
                              <td className="px-4 py-3 text-center" data-id="element-1730">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-rose-50 text-rose-700 font-bold" data-id="element-1731">
                                  {item.cantidad}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-slate-600" data-id="element-1732">
                                {precio > 0 ? `RD${precio.toLocaleString('es-DO', {
                            minimumFractionDigits: 2
                          })}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-rose-700" data-id="element-1733">
                                {valor > 0 ? `RD${valor.toLocaleString('es-DO', {
                            minimumFractionDigits: 2
                          })}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-slate-700 font-medium" data-id="element-1734">
                                {item.lote || '-'}
                              </td>
                              <td className="px-4 py-3 text-slate-700" data-id="element-1735">
                                {item.fechaVencimiento ? new Date(item.fechaVencimiento).toLocaleDateString('es-DO', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : '-'}
                              </td>
                            </tr>;
                    })}
                      </tbody>
                      <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-1736">
                        <tr data-id="element-1737">
                          <td className="px-4 py-3 text-right font-bold text-slate-700" data-id="element-1738">
                            Totales:
                          </td>
                          <td className="px-4 py-3 text-center font-black text-rose-600 text-base" data-id="element-1739">
                            {selectedVisit.devolucionItems?.reduce((sum, item) => sum + item.cantidad, 0)}
                          </td>
                          <td className="px-4 py-3" data-id="element-1740"></td>
                          <td className="px-4 py-3 text-right font-black text-rose-700 text-base" data-id="element-1741">
                            RD$
                            {(selectedVisit.devolucionItems?.reduce((sum, item) => sum + item.cantidad * ((item as any).precioUnitario || 0), 0) || 0).toLocaleString('es-DO', {
                          minimumFractionDigits: 2
                        })}
                          </td>
                          <td colSpan={2} data-id="element-1742"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter data-id="element-1743">
              <Button variant="secondary" onClick={() => setSelectedVisit(null)} data-id="element-1744">
                Cerrar
              </Button>
              <Button onClick={() => handlePrintRecibo(selectedVisit)} disabled={printingId === selectedVisit.id} leftIcon={printingId === selectedVisit.id ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" data-id="element-1746" /> : <PrinterIcon className="w-4 h-4" data-id="element-1747" />} className="bg-rose-600 hover:bg-rose-700 text-white" data-id="element-1745">
                Imprimir Recibo
              </Button>
            </ModalFooter>
          </>}
      </Modal>

      {/* Anular Recibo Modal */}
      <Modal isOpen={!!anularVisit} onClose={() => setAnularVisit(null)} title="Anular Recibo de Devolución" size="sm" data-id="element-1748">
        <ModalBody data-id="element-1749">
          <div className="text-center space-y-3" data-id="element-1750">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-rose-50 rounded-2xl" data-id="element-1751">
              <BanIcon className="w-7 h-7 text-rose-500" data-id="element-1752" />
            </div>
            <p className="text-slate-600" data-id="element-1753">
              ¿Estás seguro de anular el recibo de devolución de{' '}
              <strong data-id="element-1754">{anularVisit?.clienteNombre}</strong>?
            </p>
            <p className="text-xs text-slate-400" data-id="element-1755">
              Esta acción marcará el recibo como anulado. No se puede deshacer.
            </p>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-1756">
          <Button variant="secondary" onClick={() => setAnularVisit(null)} data-id="element-1757">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleAnularDevolucion} isLoading={isAnulando} leftIcon={<BanIcon className="w-4 h-4" data-id="element-1759" />} data-id="element-1758">
            Anular Recibo
          </Button>
        </ModalFooter>
      </Modal>

      {/* New Devolucion Modal */}
      <Modal isOpen={isNewDevModalOpen} onClose={() => !isSavingDev && setIsNewDevModalOpen(false)} title="Nueva Devolución" size="lg" data-id="element-1760">
        <ModalBody data-id="element-1761">
          <div className="space-y-6" data-id="element-1762">
            <div className="space-y-4" data-id="element-1763">
              <h3 className="text-sm font-semibold text-slate-900 border-b pb-2" data-id="element-1764">
                1. Seleccionar Cliente
              </h3>
              <SearchableSelect label="Cliente" value={newDevClient} onChange={setNewDevClient} options={clients.map(c => ({
              value: c.id,
              label: `${c.nombre} ${c.zona ? `(${c.zona})` : ''}`
            }))} placeholder="Buscar cliente..." data-id="element-1765" />
            </div>

            <div className="space-y-4" data-id="element-1766">
              <h3 className="text-sm font-semibold text-slate-900 border-b pb-2" data-id="element-1767">
                2. Agregar Productos
              </h3>
              {isLoadingProducts && <div className="flex items-center gap-2 text-sm text-slate-500 py-2" data-id="element-1768">
                  <div className="w-4 h-4 border-2 border-slate-200 border-t-rose-500 rounded-full animate-spin" data-id="element-1769" />
                  Cargando productos del inventario...
                </div>}
              {productsFetchError && <div className="text-sm text-rose-600 bg-rose-50 px-3 py-2 rounded-lg" data-id="element-1770">
                  {productsFetchError}
                </div>}
              {!isLoadingProducts && !productsFetchError && inventoryProducts.length > 0 && <p className="text-xs text-slate-400" data-id="element-1771">
                    {inventoryProducts.length} productos disponibles
                  </p>}
              <div className="relative" data-id="element-1772">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1773" />
                <input type="text" placeholder={isLoadingProducts ? 'Cargando productos...' : `Buscar entre ${inventoryProducts.length} productos...`} value={devProductSearch} disabled={isLoadingProducts || inventoryProducts.length === 0} onChange={e => {
                setDevProductSearch(e.target.value);
                setShowProductDropdown(true);
              }} onFocus={() => setShowProductDropdown(true)} className="w-full pl-9 pr-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500 disabled:bg-slate-50 disabled:text-slate-400" data-id="element-1774" />
                {showProductDropdown && devProductSearch.length > 0 && <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto" data-id="element-1775">
                    {isLoadingProducts ? <div className="p-4 text-center text-sm text-slate-500" data-id="element-1776">
                        <div className="inline-block w-4 h-4 border-2 border-slate-200 border-t-rose-500 rounded-full animate-spin mr-2" data-id="element-1777" />
                        Cargando productos...
                      </div> : (() => {
                  const filtered = inventoryProducts.filter(p => p.nombre.toLowerCase().includes(devProductSearch.toLowerCase()) || p.sku.toLowerCase().includes(devProductSearch.toLowerCase()));
                  if (filtered.length === 0) {
                    return <div className="p-4 text-center text-sm text-slate-500" data-id="element-1778">
                              No se encontraron productos para "
                              {devProductSearch}"
                            </div>;
                  }
                  return filtered.slice(0, 20).map(product => <button key={product.id} className="w-full text-left px-4 py-2.5 hover:bg-rose-50 border-b last:border-0 transition-colors" onClick={() => {
                    if (!newDevItems.find(i => i.productoId === product.id)) {
                      setNewDevItems([...newDevItems, {
                        productoId: product.id,
                        productoNombre: product.nombre,
                        productoSku: product.sku,
                        cantidad: 1,
                        precioUnitario: product.precio,
                        lote: '',
                        fechaVencimiento: ''
                      }]);
                    }
                    setDevProductSearch('');
                    setShowProductDropdown(false);
                  }} data-id="element-1779">
                            <div className="font-medium text-sm text-slate-900" data-id="element-1780">
                              {product.nombre}
                            </div>
                            <div className="text-xs text-slate-500 flex justify-between" data-id="element-1781">
                              <span data-id="element-1782">SKU: {product.sku}</span>
                              <span data-id="element-1783">
                                RD${product.precio?.toLocaleString() ?? '0'}
                              </span>
                            </div>
                          </button>);
                })()}
                  </div>}
              </div>

              {newDevItems.length > 0 && <div className="border rounded-xl overflow-hidden" data-id="element-1784">
                  <table className="w-full text-sm" data-id="element-1785">
                    <thead className="bg-slate-50 border-b" data-id="element-1786">
                      <tr data-id="element-1787">
                        <th className="px-3 py-2 text-left font-semibold text-slate-600" data-id="element-1788">
                          Producto
                        </th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-600 w-20" data-id="element-1789">
                          Cant.
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-600 w-24" data-id="element-1790">
                          Lote
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-600 w-32" data-id="element-1791">
                          Vence
                        </th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-600" data-id="element-1792">
                          Valor
                        </th>
                        <th className="px-3 py-2 w-10" data-id="element-1793"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-1794">
                      {newDevItems.map((item, idx) => <tr key={idx} className="bg-white" data-id="element-1795">
                          <td className="px-3 py-2" data-id="element-1796">
                            <p className="font-medium text-slate-900 truncate max-w-[150px]" data-id="element-1797">
                              {item.productoNombre}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-1798">
                              {item.productoSku}
                            </p>
                          </td>
                          <td className="px-3 py-2" data-id="element-1799">
                            <input type="number" min="1" value={item.cantidad} onChange={e => {
                        const newItems = [...newDevItems];
                        newItems[idx].cantidad = parseInt(e.target.value) || 1;
                        setNewDevItems(newItems);
                      }} className="w-full px-2 py-1 text-center border rounded-lg focus:outline-none focus:ring-1 focus:ring-rose-500" data-id="element-1800" />
                          </td>
                          <td className="px-3 py-2" data-id="element-1801">
                            <input type="text" value={item.lote} onChange={e => {
                        const newItems = [...newDevItems];
                        newItems[idx].lote = e.target.value;
                        setNewDevItems(newItems);
                      }} placeholder="Lote" className="w-full px-2 py-1 border rounded-lg focus:outline-none focus:ring-1 focus:ring-rose-500" data-id="element-1802" />
                          </td>
                          <td className="px-3 py-2" data-id="element-1803">
                            <input type="date" value={item.fechaVencimiento} onChange={e => {
                        const newItems = [...newDevItems];
                        newItems[idx].fechaVencimiento = e.target.value;
                        setNewDevItems(newItems);
                      }} className="w-full px-2 py-1 border rounded-lg focus:outline-none focus:ring-1 focus:ring-rose-500" data-id="element-1804" />
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-rose-700" data-id="element-1805">
                            RD$
                            {(item.cantidad * item.precioUnitario).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-center" data-id="element-1806">
                            <button onClick={() => setNewDevItems(newDevItems.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-rose-500" data-id="element-1807">
                              <XIcon className="w-4 h-4" data-id="element-1808" />
                            </button>
                          </td>
                        </tr>)}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t" data-id="element-1809">
                      <tr data-id="element-1810">
                        <td colSpan={4} className="px-3 py-2 text-right font-bold text-slate-700" data-id="element-1811">
                          Total a Acreditar:
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-rose-700" data-id="element-1812">
                          RD$
                          {newDevItems.reduce((sum, item) => sum + item.cantidad * item.precioUnitario, 0).toLocaleString()}
                        </td>
                        <td data-id="element-1813"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>}
            </div>

            <div className="space-y-4" data-id="element-1814">
              <h3 className="text-sm font-semibold text-slate-900 border-b pb-2" data-id="element-1815">
                3. Notas Adicionales
              </h3>
              <Textarea placeholder="Motivo de la devolución, observaciones..." value={newDevNotas} onChange={e => setNewDevNotas(e.target.value)} rows={3} data-id="element-1816" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-1817">
          <Button variant="secondary" onClick={() => setIsNewDevModalOpen(false)} disabled={isSavingDev} data-id="element-1818">
            Cancelar
          </Button>
          <Button onClick={handleSaveNewDevolucion} isLoading={isSavingDev} disabled={!newDevClient || newDevItems.length === 0} className="bg-rose-600 hover:bg-rose-700 text-white" data-id="element-1819">
            Guardar Devolución
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}